/**
 * Workflow trail — the analysis breadcrumb that follows the user across
 * navigations (doc 147 §5.3 PR D · world-class redesign).
 *
 * Pure list helpers: the trail is a deduped, capped sequence of `{kind, id}`
 * pairs identifying any of the 9 catalogued object kinds. Updated by the
 * route watcher in `bootstrap.ts`; rendered by the `WorkflowTrail` component
 * above each detail page's title.
 */

import type { ObjectKind } from './objectKinds.js';

export type TrailKind = ObjectKind;

export interface TrailEntry {
  kind: TrailKind;
  id: string;
}

export const TRAIL_CAP = 6;

/**
 * Append `entry` to the trail, deduping a consecutive duplicate (same kind
 * AND id), and capping at {@link TRAIL_CAP}. Returns a new array.
 */
export function pushTrail(trail: readonly TrailEntry[], entry: TrailEntry): TrailEntry[] {
  const last = trail[trail.length - 1];
  if (last && last.kind === entry.kind && last.id === entry.id) return [...trail];
  const next = [...trail, entry];
  return next.length > TRAIL_CAP ? next.slice(next.length - TRAIL_CAP) : next;
}

/** Drop the last entry. Empty trails stay empty. */
export function popTrail(trail: readonly TrailEntry[]): TrailEntry[] {
  return trail.length === 0 ? [] : trail.slice(0, -1);
}
